import { BOARD_LABELS, BOARD_SLUGS } from '@tomato/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { useSessionStore } from '../store/session';
import { useTomatoHuntStore } from '../store/tomatoHunt';
import { AddToHomeBanner } from './AddToHomeBanner';
import { MobileTabBar } from './MobileTabBar';
import { PasskeyNudgeBanner } from './PasskeyNudgeBanner';
import { PullToRefresh } from './PullToRefresh';
import { SiteFooter } from './SiteFooter';
import { TomatoHuntCelebration, TomatoHuntIcon } from './TomatoHunt';
import { Button } from './ui';

/** 스크롤이 threshold를 넘었는가 — 라지 타이틀 → 컴팩트 헤더 타이틀 전환용. */
function useScrolled(threshold: number): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > threshold);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, [threshold]);
  return scrolled;
}

/** 경로 → 컴팩트 타이틀. 스크롤하면 헤더 중앙에 미니어처로 뜬다(iOS 라지 타이틀 패턴). */
function compactTitleFor(pathname: string): string | null {
  if (pathname.startsWith('/market')) return BOARD_LABELS.market;
  if (pathname.startsWith('/book')) return BOARD_LABELS.book;
  if (pathname.startsWith('/talk')) return BOARD_LABELS.talk;
  if (pathname.startsWith('/notice')) return BOARD_LABELS.notice;
  if (pathname.startsWith('/settings')) return '마이페이지';
  if (pathname.startsWith('/favorites')) return '찜한 상품';
  if (pathname.startsWith('/chats')) return '대화';
  if (pathname.startsWith('/harvest')) return '토마토 따기';
  return null;
}

/**
 * 토마토 잔액 뱃지 — 값이 오르면 숫자가 굴러 올라가며(카운트업) 통통 튄다.
 * 적립의 "받았다!" 순간을 시각 피드백으로. 리마운트 키는 안쪽 span에만 건다
 * (바깥을 리마운트하면 카운트업 상태가 초기화된다).
 */
function BalanceBadge({ value }: { value: number }) {
  const [display, setDisplay] = useState(value);
  const [bumpKey, setBumpKey] = useState(0);
  const prevRef = useRef(value);

  useEffect(() => {
    const from = prevRef.current;
    if (from === value) return;
    prevRef.current = value;
    if (value > from) setBumpKey((k) => k + 1); // 증가할 때만 튄다(감소 연출은 없음)

    const start = performance.now();
    const durationMs = 600;
    let raf = 0;
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - (1 - p) ** 3;
      setDisplay(Math.round(from + (value - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return (
    <span className="rounded-full bg-tomato-50 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-tomato-600">
      <span key={bumpKey} className={bumpKey > 0 ? 'badge-bump' : undefined}>
        {display}🍅
      </span>
    </span>
  );
}

/**
 * 공통 껍데기. 상단 바는 스크롤해도 붙어 있고 배경을 반투명 블러로 처리해
 * 본문이 그 아래로 흘러가는 게 보이게 한다(내용이 주인공, 크롬은 물러남).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const me = useSessionStore((s) => s.me);
  const logout = useSessionStore((s) => s.logout);
  const loadHunt = useTomatoHuntStore((s) => s.load);
  const navigate = useNavigate();
  const location = useLocation();
  const scrolled = useScrolled(56);
  const compactTitle = compactTitleFor(location.pathname);

  // 로그인·온보딩을 마친 뒤 껍데기가 뜰 때 1회 오늘의 토마토 위치를 받아온다.
  useEffect(() => {
    void loadHunt();
  }, [loadHunt]);

  return (
    <div className="min-h-full">
      <header className="vt-header sticky top-0 z-20 bg-surface/80 backdrop-blur-xl hairline-b">
        <div className="relative mx-auto flex h-14 max-w-2xl items-center justify-between gap-2 px-5">
          {/* 컴팩트 타이틀 — 라지 타이틀이 스크롤로 사라지면 헤더 중앙에 미니어처로 올라온다(모바일). */}
          {compactTitle && (
            <span
              aria-hidden
              className={`pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 text-[15px] font-semibold tracking-tight text-ink-900 transition-all duration-200 ease-(--ease-out-soft) sm:hidden ${
                scrolled ? '-translate-y-1/2 opacity-100' : 'translate-y-[calc(-50%+8px)] opacity-0'
              }`}
            >
              {compactTitle}
            </span>
          )}
          <div className="flex min-w-0 items-center gap-1">
            <button
              onClick={() => navigate('/market', { viewTransition: true })}
              aria-label="홈으로"
              title="홈으로"
              className="flex cursor-pointer items-center whitespace-nowrap text-[15px] font-semibold tracking-tight text-ink-900 transition duration-150 hover:opacity-70 active:scale-95"
            >
              <span className="mr-1">🍅</span>
              <span className="max-sm:text-[14px]">토마토 마켓</span>
              {/* 소개 문구 — 푸터에서 올라왔다. 모바일에선 컴팩트 타이틀에 자리를 내주며 사라진다. */}
              <span
                className={`ml-1.5 min-w-0 truncate text-[11px] font-normal text-ink-400 transition-opacity duration-200 ${
                  scrolled && compactTitle ? 'max-sm:opacity-0' : ''
                }`}
              >
                · 헥토 사내 벼룩시장
              </span>
            </button>
            <TomatoHuntIcon elementId="header-brand" />
          </div>
          <div className="flex items-center gap-1">
            {/* 언제든 들어갈 수 있으므로 상시 노출. 모바일 헤더는 좁으니 바구니 하나로 줄인다
                (예전엔 수확철이 열린 동안만 하단 진입 바가 이 역할을 했다). */}
            <NavLink
              viewTransition
              to="/harvest"
              aria-label="토마토 따기"
              className={({ isActive }) =>
                `whitespace-nowrap px-2 text-[13px] transition ${isActive ? 'text-tomato-600' : 'text-ink-500 hover:text-ink-900'}`
              }
            >
              <span className="max-sm:hidden">🍅토마토 따기🍅</span>
              <span className="text-[17px] sm:hidden" aria-hidden>
                🧺
              </span>
            </NavLink>
            <NavLink
              viewTransition
              to="/chats"
              className={({ isActive }) =>
                `whitespace-nowrap px-2 text-[13px] transition ${isActive ? 'text-tomato-600' : 'text-ink-500 hover:text-ink-900'}`
              }
            >
              대화
            </NavLink>
            <NavLink
              viewTransition
              to="/favorites"
              className={({ isActive }) =>
                `whitespace-nowrap px-2 text-[13px] transition ${isActive ? 'text-tomato-600' : 'text-ink-500 hover:text-ink-900'}`
              }
            >
              찜
            </NavLink>
            {me?.role === 'admin' && (
              <NavLink
                viewTransition
                to="/admin"
                className={({ isActive }) =>
                  `px-2 text-[13px] transition ${isActive ? 'text-tomato-600' : 'text-ink-400 hover:text-ink-700'}`
                }
              >
                관리
              </NavLink>
            )}
            <NavLink
              viewTransition
              to="/settings"
              className={({ isActive }) =>
                `flex items-center gap-1.5 whitespace-nowrap px-2 text-[13px] transition ${isActive ? 'text-tomato-600' : 'text-ink-500 hover:text-ink-900'}`
              }
            >
              {/* 좁은 화면에선 잔액 뱃지만 — 닉네임까지 넣으면 넘친다. */}
              <span className="max-sm:hidden">{me?.nickname}</span>
              {typeof me?.tomatoBalance === 'number' && <BalanceBadge value={me.tomatoBalance} />}
            </NavLink>
            {/* 모바일에선 마이페이지 안에서 로그아웃한다. */}
            <Button
              variant="ghost"
              size="sm"
              className="max-sm:hidden"
              onClick={() => void logout()}
            >
              로그아웃
            </Button>
          </div>
        </div>

        {/* 게시판 탭 — 모바일에선 하단 탭바가 대신한다(같은 순서·라벨). */}
        <nav className="mx-auto flex max-w-2xl gap-6 px-5 max-sm:hidden">
          {BOARD_SLUGS.map((slug) => (
            <NavLink
              key={slug}
              viewTransition
              to={`/${slug}`}
              className={({ isActive }) =>
                `relative py-2.5 text-[14px] font-medium transition ${
                  isActive ? 'text-ink-900' : 'text-ink-400 hover:text-ink-700'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {BOARD_LABELS[slug]}
                  {isActive && (
                    <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-tomato-600" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </header>

      {/* 당겨서 새로고침 — 홈 화면 앱엔 새로고침 버튼이 없다. */}
      <PullToRefresh />

      {/* 모바일은 하단 탭바 높이만큼 여백을 더 준다. */}
      <main className="mx-auto max-w-2xl px-5 pb-16 max-sm:pb-28">{children}</main>
      <SiteFooter />
      <TomatoHuntCelebration />

      {/* 하단 고정 스택 — 위에서부터 [iOS 홈 화면 추가 배너][패스키 넛지][모바일 탭바].
          한 컨테이너에 쌓아 서로 겹치지 않는다. 컨테이너는 클릭을 막지 않는다.
          앞의 둘은 동시에 뜨지 않는다 — 패스키 넛지가 홈 화면 추가에 자리를 양보한다. */}
      <div className="vt-bottom pointer-events-none fixed inset-x-0 bottom-0 z-40 flex flex-col">
        <AddToHomeBanner />
        <PasskeyNudgeBanner />
        <MobileTabBar />
      </div>
    </div>
  );
}
